import { Card } from "@/ingot";
import { useTranslation } from "react-i18next";

import { useCartAddons, useUpdateCartAddons } from "@/api/queries";

import { useDisplayMoney } from "@/lib/currency";

/**
 * Objednávkové příplatky (KAN-142) — co si zákazník může přikoupit
 * k CELÉ zakázce, vedle vyrobených dílů.
 *
 * Komponenta **neví, jaký příplatek to je.** Dnes je jediný (protokol
 * o kontrole), ale nabídky přispívají zdroje na BE a tenhle soubor
 * vykresluje, co přijde — pojmenovat tu protokol by byl týž hardcode,
 * jakému se vyhýbá `order_addons.py` (GENERIC_PLATFORM).
 *
 * **Nezobrazí se vůbec**, když tenant nic nenacenil. Prázdná sekce
 * s nadpisem „Příplatky" a ničím pod ním vypadá jako rozbitá stránka;
 * výchozí stav platformy je přitom právě ten prázdný.
 *
 * Posílá se celý výběr, ne delta — viz `useUpdateCartAddons`.
 */
export function CartAddonsSection(): JSX.Element | null {
  const { t, i18n } = useTranslation("cart");
  const { format } = useDisplayMoney();
  const addonsQuery = useCartAddons();
  const updateAddons = useUpdateCartAddons();

  const addons = addonsQuery.data?.addons ?? [];
  if (addons.length === 0) return null;

  const selectedKeys = addons.filter((a) => a.selected).map((a) => a.key);

  const toggle = (key: string, next: boolean) => {
    const keys = next
      ? [...selectedKeys, key]
      : selectedKeys.filter((k) => k !== key);
    updateAddons.mutate(keys);
  };

  return (
    <Card padded data-testid="cart-addons-section">
      <h2 className="mono block text-[11px] uppercase tracking-[0.08em] text-ink-3">
        {t("addons.title", { defaultValue: "Příplatky" })}
      </h2>
      <p className="mt-1 text-xs text-ink-3">
        {t("addons.hint", {
          defaultValue: "Volitelné položky k celé objednávce.",
        })}
      </p>
      <ul className="mt-3 space-y-2">
        {addons.map((addon) => {
          const inputId = `cart-addon-${addon.key}`;
          return (
            <li key={addon.key}>
              <label
                htmlFor={inputId}
                className="flex items-baseline justify-between gap-3 text-sm"
              >
                <span className="flex items-baseline gap-2">
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={addon.selected}
                    // Během ukládání se needituje: doběhlý PATCH vrací
                    // stav PO uložení a dvě rychlá kliknutí by se jinak
                    // přepsala navzájem.
                    disabled={updateAddons.isPending}
                    onChange={(e) => toggle(addon.key, e.target.checked)}
                    data-testid={`cart-addon-toggle-${addon.key}`}
                    className="h-4 w-4 rounded border-border-strong text-accent focus:ring-accent"
                  />
                  <span className="text-ink">
                    {/* Popisek autoruje ten, kdo příplatek nabízí.
                        Fallback na ``cs`` a pak na klíč, ať řádek nikdy
                        nevyjde prázdný. */}
                    {addon.label_i18n[i18n.language] ??
                      addon.label_i18n.cs ??
                      addon.key}
                  </span>
                </span>
                <span className="mono tabular-nums text-ink">
                  {format(addon.price_czk)}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
